import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';
import { deriveMembershipYear } from 'src/modules/sending/functions/membershipYear';
import { SendingFriPeriodTimeline } from 'src/modules/sending/components/sendingFriPeriodTimeline';
import { SendingDatePicker } from 'src/modules/sending/components/sendingDatePicker';

interface SendingOrderDateModalProps {
  open: boolean;
  onClose: () => void;
  initialDateId: number | undefined;
  initialSendDate: Dayjs | null;
  initialPayDate: Dayjs | null;
  onSave: (dateId: number, sendDate: Dayjs, payDate: Dayjs) => void;
}

// Modal för att ändra utskicksdatum + betaldatum, med tidslinje och friperiod-validering. Arbetar mot lokal
// state och committar först vid Spara (så Avbryt inte ändrar beställningen).
export const SendingOrderDateModal = ({ open, onClose, initialDateId, initialSendDate, initialPayDate, onSave }: SendingOrderDateModalProps) => {
  const { membershipMMdd, gracePeriodDays } = useSendingWizard();
  const [dateId, setDateId] = useState<number | undefined>(initialDateId);
  const [sendDate, setSendDate] = useState<Dayjs | null>(initialSendDate);
  const [payDate, setPayDate] = useState<Dayjs | null>(initialPayDate);

  // Initiera lokal state när modalen öppnas.
  useEffect(() => {
    if (open) {
      setDateId(initialDateId);
      setSendDate(initialSendDate);
      setPayDate(initialPayDate);
    }
  }, [open, initialDateId, initialSendDate, initialPayDate]);

  const result = useMemo(
    () => deriveMembershipYear({ sendDate, payDate, expireMMdd: membershipMMdd, graceDays: gracePeriodDays }),
    [sendDate, payDate, membershipMMdd, gracePeriodDays],
  );

  const payBeforeSend = !!(sendDate && payDate && payDate.isBefore(sendDate, 'day'));
  const straddles = !!result?.straddles;
  const canSave = !!dateId && !!sendDate && !!payDate && !payBeforeSend && !straddles;

  const fmt = (d: Dayjs) => d.format('YYYY-MM-DD');

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Ändra utskicksdatum</DialogTitle>
      <DialogContent dividers>
        <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block', mb: 1 }}>
          Utskicksdatum
        </Typography>
        <SendingDatePicker
          value={dateId}
          onChange={(id, date) => {
            setDateId(id);
            setSendDate(date);
            // Föreslå betaldatum = utskick + 35 dagar om det saknas eller hamnar före utskicket.
            if (!payDate || payDate.isBefore(date, 'day')) setPayDate(date.add(35, 'day'));
          }}
        />

        <Divider sx={{ my: 2.5 }} />

        <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 700, letterSpacing: 0.6, display: 'block', mb: 1 }}>
          Sista betaldatum (förfallodatum på fakturorna)
        </Typography>
        <Box sx={{ maxWidth: 260 }}>
          <DatePicker
            value={payDate}
            onChange={(v) => setPayDate(v)}
            minDate={sendDate ?? undefined}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Box>

        {sendDate && result && (
          <Box sx={{ mt: 3 }}>
            <SendingFriPeriodTimeline sendDate={sendDate} payDate={payDate} result={result} />
          </Box>
        )}

        {payBeforeSend && (
          <Alert severity='error' sx={{ mt: 2 }}>
            Sista betaldatum får inte ligga före utskicksdatumet.
          </Alert>
        )}

        {straddles && result && (
          <Alert severity='error' sx={{ mt: 2 }}>
            Friperioden börjar {fmt(result.friStart)} och hamnar då inom betalningsfönstret. Då skulle medlemmar som betalar tidigt få medlemsår{' '}
            {result.expiryYear - 1} och de som betalar under friperioden få {result.expiryYear} – från samma avisering. Välj ett sista betaldatum
            senast {fmt(result.latestSafePayDate)} (eller flytta hela utskicket in i friperioden).
          </Alert>
        )}

        {!straddles && !payBeforeSend && result && (
          <Alert severity='info' sx={{ mt: 2 }}>
            Aviseringen gäller <b>medlemsår {result.expiryYear}</b> (förfallodatum {fmt(result.expiryDate)}).
            {result.hasFriPeriod ? ` Friperioden börjar ${fmt(result.friStart)}.` : ' Förbundet har ingen friperiod.'}
          </Alert>
        )}

        {!straddles && !payBeforeSend && result?.latePaymentSpill && (
          <Alert severity='warning' sx={{ mt: 2 }}>
            Obs: sena betalningar som kommer in under friperioden (fr.o.m. {fmt(result.friStart)}) ger nästa års medlemskap ({result.expiryYear + 1}).
            Vill du undvika det kan du välja ett sista betaldatum med god marginal före friperioden.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color='inherit' onClick={onClose}>
          Avbryt
        </Button>
        <Button
          variant='contained'
          disableElevation
          disabled={!canSave}
          onClick={() => {
            if (dateId && sendDate && payDate) onSave(dateId, sendDate, payDate);
          }}
        >
          Spara
        </Button>
      </DialogActions>
    </Dialog>
  );
};
